import { Hidden, Modal } from '@mui/material'
import IonIcon from '@reacticons/ionicons'
import detectIncognito from 'detectincognitojs'
import mixpanel from 'mixpanel-browser'
import { useRouter } from 'next/router'
import React, { Dispatch, useCallback, useEffect, useRef, useState } from 'react'
import { TailSpin } from 'react-loading-icons'
import { useDispatch, useSelector } from 'react-redux'
import Drawer from 'src/components/global/Drawer/Drawer'
import { Filter } from 'src/components/global/filter'
import { LoadingAnimation } from 'src/components/global/LoadingAnimation'
import { SearchField } from 'src/components/global/SearchField'
import { Sidebar } from 'src/components/global/Sidebar'
import { SortbyDropdown } from 'src/components/global/SortbyDropdown'
import { AMPLIFY_USER_POOL_APP_CLIENT_ID, MIXPANEL_ENABLED, SHOW_FREE_SEARCH } from 'src/config/config'
import {
  IEnum_Solution_Type,
  ISolutionListItemFragment,
  useSearchSolutionsLazyQuery,
} from 'src/graphql/generated/hooks'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { useDecodedKeyword } from 'src/hooks/useGetDecodeKeyword'
import { useUpdateQueryParams } from 'src/hooks/useUpdateQueryParams'
import {
  buildCustomTagExportBaseName,
  exportSolutionsCsv,
  getSolutionsExportDownloadMeta,
} from 'src/services/common/exportTopicsCsv'
import { showToast } from 'src/store/actions/toastAction'
import { AppState } from 'src/store/types'
import { CompareInfos } from 'src/store/types/compare'
import { FilterOption, TaxonomyType } from 'src/types'

import { CompareModalContent } from '../CompareModalContent/CompareModalContent'
import useSearchQuery from '../CustomHooks/SearchQuery'
import { getFingerprint, useFingerprint } from '../CustomHooks/useFingerprint'
import { FilterOptions } from '../FilterOptions/FilterOptions'
import { PremiumModal } from '../global/PremiumModal'
import { ThemeOnly } from '../global/ThemeOnly'
import { Pagination } from '../Pagination/Pagination'
import FreeSearchPopup from '../Popup/FreeSearchPopup'
import { VendorList } from '../VendorList/VendorList'

export type SearchPaginationProps = {
  filterProps: FilterOption[]
  defaultSelectFilterIdx?: number
  companyId?: string
  companyName?: string
  followOnly: boolean
  includeCompany: boolean
  isLazyLoad?: boolean
  setFilterHeight?: (value: boolean) => void
  isFrom?: string
  refreshFilterData?: () => void
  activeTab?: string | null
  showExportButton?: boolean
  searchKeyword?: string
  sharedPortfolioId?: string
  sharedPortfolioSlug?: string
  isSharedPortfolio?: boolean
  selectedCustomTagDocumentIds?: string[] | null
  refetchSideBarTagsData?: () => void
  customTagId?: string
  customTagName?: string
  /** Solution documentIds on this custom tag (UAT-style custom-tag page scoping). */
  selectedSolutionDocumentIds?: string[]
  refetchCustomTagData?: () => void
}

export const SearchPagination: React.FC<SearchPaginationProps> = ({
  filterProps,
  defaultSelectFilterIdx = 1,
  companyId,
  companyName,
  followOnly,
  includeCompany,
  setFilterHeight,
  isFrom = '',
  refreshFilterData,
  activeTab = null,
  showExportButton = false,
  searchKeyword = '',
  sharedPortfolioId = undefined,
  sharedPortfolioSlug = undefined,
  isSharedPortfolio = true,
  selectedCustomTagDocumentIds = null,
  refetchSideBarTagsData,
  customTagId = '',
  customTagName = '',
  selectedSolutionDocumentIds = [],
  refetchCustomTagData = () => {},
}) => {
  const selectedDocumentIdsKey = selectedSolutionDocumentIds.join(',')
  const isCustomTagWithNoDocuments = isFrom === 'custom-tag' && selectedSolutionDocumentIds.length === 0
  const router = useRouter()
  const fingerprintReady = useFingerprint()
  const [isNonLogin, setIsNonLogin] = useState(false)
  const [fingerprint, setFingerprint] = useState<string | null>(null)
  // const [browser, setBrowser] = useState({ browserName: '', isPrivate: false })
  const [isFreeSearch, setIsFreeSearch] = useState<boolean | null>(null)
  const { getSearchQuery } = useSearchQuery()
  const { updateQueryParams } = useUpdateQueryParams()

  const { session } = useAuthenticated()
  const confirmed = session?.user?.confirmed || false

  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [items, setItems] = useState<ISolutionListItemFragment[]>([])
  const [postsPerPage] = useState(10)

  const displayItems =
    selectedCustomTagDocumentIds && selectedCustomTagDocumentIds.length > 0
      ? items.filter((item) => item.documentId && selectedCustomTagDocumentIds.includes(item.documentId))
      : items

  const [showFilterDlg, setShowFilterDlg] = useState(false)
  const [isVisible, setIsVisible] = React.useState(false)
  const closeDrawer = useCallback(() => setIsVisible(false), [])
  const [loadSolutions, { loading, data: searchSolutions }] = useSearchSolutionsLazyQuery({
    fetchPolicy: 'no-cache',
  })
  const [showNoMessage, setShowNoMessage] = useState('')
  const [showFoundMessage, setShowFoundMessage] = useState('')
  const [filterPropsData, setFilterPropsData] = useState<FilterOption[]>([])
  const [isPremiumWithPremiumOrganization, setIsPremiumWithPremiumOrganization] = useState<boolean | null>(null)

  const compareInfos: CompareInfos = useSelector((state: AppState) => state.compareInfos)
  const authInfo = useSelector((state: AppState) => state.authInfo)
  const compareInfosLength = useRef(compareInfos.solutions.length)
  const { isPremium } = useAuthenticated()
  const dispatch: Dispatch<any> = useDispatch()

  // Add useDecodedKeyword for internal use when searchKeyword not passed:
  const decodedKeyword = useDecodedKeyword(router.query.keyword)
  const keywordForDisplay = searchKeyword ?? decodedKeyword

  useEffect(() => {
    if (authInfo?.isPremium && authInfo?.premiumOrganizationId && authInfo?.premiumOrganizationName) {
      setIsPremiumWithPremiumOrganization(true)
    } else {
      setIsPremiumWithPremiumOrganization(false)
    }
  }, [authInfo])

  useEffect(() => {
    setFilterPropsData(filterProps)
  }, [filterProps])

  useEffect(() => {
    if (fingerprintReady && SHOW_FREE_SEARCH) {
      setFingerprint(fingerprintReady)
    }
  }, [fingerprintReady])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const lastAuthUser = localStorage.getItem(
        `CognitoIdentityServiceProvider.${AMPLIFY_USER_POOL_APP_CLIENT_ID}.LastAuthUser`,
      )
      setIsNonLogin(!lastAuthUser) // Safely set the boolean value
      // getfingerprintID(!SHOW_FREE_ARTICLE)
    }
  }, [])

  useEffect(() => {
    function isObjectEmpty(obj: Record<string, any>) {
      return Object.keys(obj).every((key) => {
        if (key === 'page' || key === 'sort') return true
        else if (key === '' || !obj[key] || obj[key].trim() === '') return true
        else return false
      })
    }
    if (followOnly && isObjectEmpty(router.query)) {
      const message = isPremium ? 'found' : 'followed'
      setShowNoMessage(`No solution ${message}`)

      setShowFoundMessage(totalCount > 1 ? `Solutions ${message}` : `Solution ${message}`)
    } else {
      setShowNoMessage('No solution found')
      setShowFoundMessage(totalCount > 1 ? 'Solutions found' : 'Solution found')
    }
  }, [router.query, followOnly, totalCount])

  const getNamesFromIds = (type: string, ids?: string) => {
    if (!ids) return []
    const taxonomy = filterPropsData.find((item) => item.title === type)
    const idArray = ids.split(',')
    const result: string[] = []
    if (taxonomy) {
      idArray.map((id) => {
        const res = taxonomy.data.find((item) => item.id === id)
        if (res) {
          result.push(res.name)
        }
      })
    }
    return [...new Set(result)]
  }

  const getSubTopicNamesFromIds = (ids?: string) => {
    if (!ids) return []
    const idArray = ids.split(',')
    if (idArray.length === 0) return []
    const result: string[] = []
    const topics = filterPropsData.find((item) => item.title === TaxonomyType.ProductType)?.data
    topics?.map((topic: FilterOption['data'][number]) => {
      const data = topic as { subTopics?: Array<{ id?: string; name?: string }> }
      data?.subTopics?.map((subTopic) => {
        if (subTopic?.id && idArray.includes(subTopic.id) && subTopic?.name) {
          result.push(subTopic.name)
        }
      })
    })
    return [...new Set(result)]
  }

  const getSolutions = async (setStateFlag: boolean = false) => {
    const browser = await detectIncognito()
    // await detectIncognito().then((result: any) => {
    //   browser = result
    // })
    const fingerprintID = fingerprint ? fingerprint : await getFingerprint()
    const curPage = router.query.page !== undefined ? parseInt(router.query.page as string) : 1
    const indexOfLastPost = curPage * postsPerPage
    const indexOfFirstPost = indexOfLastPost - postsPerPage
    const filters = router.query

    let selection: string[] = []
    if (filters.productType) {
      const paths = (filters.productType as string).split('_')
      const checks = paths[0].split(',')

      checks.map((check) => {
        const subTopicDocumentId = check.split('@@@@')[1]
        selection.push(subTopicDocumentId)
      })
    }
    selection = [...new Set(selection)]
    const newProductType = selection.join(',')
    const baseQuery = {
      offset: indexOfFirstPost,
      limit: postsPerPage,
      keyword: filters !== undefined ? (filters.keyword as string) : undefined,
      audiences: filters !== undefined ? (filters.audiences as string) : undefined,
      practiceAreas: filters !== undefined ? (filters.practiceAreas as string) : undefined,
      languages: filters !== undefined ? (filters.languages as string) : undefined,
      deployments: filters !== undefined ? (filters.deployments as string) : undefined,
      attributes: filters !== undefined ? (filters.attributes as string) : undefined,
      regionsServed: filters !== undefined ? (filters.regionsServed as string) : undefined,
      sort: filters !== undefined ? (filters.sort as string) || 'Default' : 'Default',
      topic: filters !== undefined ? (filters.topics as string) : undefined,
      subTopic: filters !== undefined ? (newProductType as string) : undefined,
      hqs: filters !== undefined ? (filters.hqs as string) : undefined,
      sponsorLevels: filters !== undefined ? (filters.sponsorLevels as string) : undefined,
      followOnly: authInfo.idToken ? followOnly : false,
      integrations: filters !== undefined ? (filters.integrations as string) : undefined,
      company: companyId,
      includeCompany,
      sharedPortfolioId,
      customTags: filters !== undefined ? (filters?.customTags as string) : undefined,
      followTabs: followOnly && activeTab && (activeTab != 'All' || activeTab != null) ? activeTab : undefined,
      documentIds: selectedSolutionDocumentIds,
    }
    const customAccessQuery: string = getSearchQuery(filters, newProductType)
    const query =
      SHOW_FREE_SEARCH == true && fingerprintID && fingerprintID != null && customAccessQuery != ''
        ? {
            ...baseQuery,
            accessFreeSearch: true,
            uniqueAddress: !browser?.isPrivate && browser?.browserName !== '' ? fingerprintID : '',
            customAccessQuery: customAccessQuery,
          }
        : baseQuery

    if (MIXPANEL_ENABLED) {
      mixpanel.track('search_content', {
        keyword: query.keyword,
        audiences: getNamesFromIds(TaxonomyType.Audience, query.audiences),
        practiceAreas: getNamesFromIds(TaxonomyType.PracticeArea, query.practiceAreas),
        languages: getNamesFromIds(TaxonomyType.Language, query.languages),
        deployments: getNamesFromIds(TaxonomyType.Deployment, query.deployments),
        attributes: getNamesFromIds(TaxonomyType.Attribute, query.attributes),
        regionsServed: getNamesFromIds(TaxonomyType.RegionsServed, query.regionsServed),
        subTopic: getSubTopicNamesFromIds(query.subTopic),
        hqs: getNamesFromIds(TaxonomyType.HQ, query.hqs),
      })
    }
    const authHeader = authInfo.idToken ? { Authorization: `Bearer ${authInfo.idToken}` } : undefined
    if (setStateFlag) {
      const result = await loadSolutions({
        variables: { query },
        fetchPolicy: 'no-cache',
        ...(authHeader ? { context: { headers: authHeader } } : {}),
      })
      const nextItems = (result?.data?.searchSolutions?.solutions || []).filter(
        Boolean,
      ) as unknown as ISolutionListItemFragment[]
      setItems(nextItems)
      setTotalCount(result?.data?.searchSolutions?.total || 0)
    } else {
      await loadSolutions({
        variables: { query },
        fetchPolicy: 'no-cache',
        ...(authHeader ? { context: { headers: authHeader } } : {}),
      })
    }
  }

  useEffect(() => {
    if (!router.isReady) {
      return
    }
    // Shared portfolio detail: portfolio loads async; first fetch must not run without id or we get unscoped follow results.
    if (isFrom === 'my-shared-portfolio' && !sharedPortfolioId) {
      return
    }
    if (isCustomTagWithNoDocuments) {
      setItems([])
      setTotalCount(0)
      return
    }
    // setToken(authInfo.idToken)
    // Reset to page 1 when sort changes (page parameter is undefined)
    if (router.query.page === undefined || router.query.page === '') {
      setCurrentPage(1)
    } else if (router.query.page !== undefined && router.query.page !== '') {
      setCurrentPage(parseInt(router.query.page as string))
    }

    getSolutions()
  }, [
    currentPage,
    authInfo.idToken,
    router.isReady,
    router.query,
    activeTab,
    isFrom,
    sharedPortfolioId,
    selectedDocumentIdsKey,
  ])

  useEffect(() => {
    if (loading) {
      return
    }
    if (isCustomTagWithNoDocuments) {
      setItems([])
      setTotalCount(0)
      return
    }
    if (router.query.page !== undefined) {
      setCurrentPage(parseInt(router.query.page as string))
    }
    let isFreeSearch = null
    if (
      SHOW_FREE_SEARCH &&
      (searchSolutions?.searchSolutions?.isFreeSearch == true ||
        searchSolutions?.searchSolutions?.isFreeSearch == false)
    ) {
      isFreeSearch = searchSolutions?.searchSolutions?.isFreeSearch
    }
    if (typeof setFilterHeight == 'function') {
      if (isFreeSearch == false) {
        setFilterHeight(true)
      } else {
        setFilterHeight(false)
      }
    }
    setIsFreeSearch(isFreeSearch)
    const nextItems = (searchSolutions?.searchSolutions?.solutions || []).filter(
      Boolean,
    ) as unknown as ISolutionListItemFragment[]

    if (followOnly) {
      authInfo.idToken && setItems(nextItems)
    } else {
      setItems(nextItems)
    }
    setTotalCount(searchSolutions?.searchSolutions?.total || 0)

    if (
      !confirmed &&
      searchSolutions &&
      searchSolutions?.searchSolutions?.isFreeSearch == true &&
      searchSolutions?.searchSolutions?.isFreeSearchLeft == 1
    ) {
      dispatch(showToast('This is your last free search for the week. ', 'non_r_last_free_search'))
    }
    if (
      confirmed &&
      searchSolutions &&
      searchSolutions?.searchSolutions?.isFreeSearch == true &&
      searchSolutions?.searchSolutions?.isFreeSearchLeft == 1
    ) {
      dispatch(showToast('This is your last free search for the week. ', 'r_last_free_search'))
    }
  }, [loading, searchSolutions, isCustomTagWithNoDocuments])

  useEffect(() => {
    const pathname = router.pathname
    if (
      compareInfos.solutions.length > 0 &&
      confirmed &&
      pathname !== '/my-followings' &&
      pathname !== '/my-portfolios' &&
      compareInfos.solutions.length !== compareInfosLength.current
    ) {
      setIsVisible(true)
    } else {
      setIsVisible(false)
    }
  }, [compareInfos])

  useEffect(() => {
    if (
      compareInfos.solutions.length > 0 &&
      confirmed &&
      compareInfos.solutions.length !== compareInfosLength.current
    ) {
      setIsVisible(true)
    } else {
      setIsVisible(false)
    }
  }, [compareInfos])

  const navigateToPage = (page: number) => {
    updateQueryParams({ page: page.toString() }, true)
  }

  // Change page
  const paginateFront = () => {
    const lastpage = totalCount / postsPerPage
    if (currentPage < lastpage) {
      setCurrentPage(currentPage + 1)
      navigateToPage(currentPage + 1)
    }
  }
  const paginateBack = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
      navigateToPage(currentPage - 1)
    }
  }
  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber)
    navigateToPage(pageNumber)
  }
  const [showPremiumModal, setShowPremiumModal] = useState(false)

  const handleClosePremiumModal = () => {
    setShowPremiumModal(false)
  }

  const exportSolutionsINCsv = async () => {
    if (!isPremium) {
      setShowPremiumModal(true)
    } else {
      const idToken = authInfo.idToken?.trim()
      if ((followOnly || isFrom === 'custom-tag') && !idToken) {
        dispatch(showToast('Please sign in again to export your followed solutions.', 'error'))
        return
      }
      const browser = await detectIncognito()
      // await detectIncognito().then((result: any) => {
      //   browser = result
      // })
      const fingerprintID = fingerprint ? fingerprint : await getFingerprint()
      const curPage = router.query.page !== undefined ? parseInt(router.query.page as string) : 1
      const indexOfLastPost = curPage * postsPerPage
      const indexOfFirstPost = indexOfLastPost - postsPerPage
      const filters = router.query

      let selection: string[] = []
      if (filters.productType) {
        const paths = (filters.productType as string).split('_')
        const checks = paths[0].split(',')

        checks.map((check) => {
          const subTopicDocumentId = check.split('@@@@')[1]
          selection.push(subTopicDocumentId)
        })
      }
      selection = [...new Set(selection)]
      const newProductType = selection.join(',')
      const baseQuery = {
        offset: indexOfFirstPost,
        limit: postsPerPage,
        keyword: filters !== undefined ? (filters.keyword as string) : undefined,
        audiences: filters !== undefined ? (filters.audiences as string) : undefined,
        practiceAreas: filters !== undefined ? (filters.practiceAreas as string) : undefined,
        languages: filters !== undefined ? (filters.languages as string) : undefined,
        deployments: filters !== undefined ? (filters.deployments as string) : undefined,
        attributes: filters !== undefined ? (filters.attributes as string) : undefined,
        regionsServed: filters !== undefined ? (filters.regionsServed as string) : undefined,
        sort: filters !== undefined ? (filters.sort as string) || 'Default' : 'Default',
        topic: filters !== undefined ? (filters.topics as string) : undefined,
        subTopic: filters !== undefined ? (newProductType as string) : undefined,
        hqs: filters !== undefined ? (filters.hqs as string) : undefined,
        sponsorLevels: filters !== undefined ? (filters.sponsorLevels as string) : undefined,
        followOnly: authInfo.idToken ? followOnly : false,
        integrations: filters !== undefined ? (filters.integrations as string) : undefined,
        company: companyId,
        includeCompany,
        sharedPortfolioId,
        customTags: filters !== undefined ? (filters?.customTags as string) : undefined,
        followTabs: followOnly && activeTab && (activeTab != 'All' || activeTab != null) ? activeTab : undefined,
        documentIds: selectedSolutionDocumentIds,
        ...(isFrom === 'custom-tag' && customTagId ? { customTagExport: true, customTagId } : {}),
      }
      const customAccessQuery: string = getSearchQuery(filters, newProductType)
      const query =
        SHOW_FREE_SEARCH == true && fingerprintID && fingerprintID != null && customAccessQuery != ''
          ? {
              ...baseQuery,
              accessFreeSearch: true,
              uniqueAddress: !browser?.isPrivate && browser?.browserName !== '' ? fingerprintID : '',
              customAccessQuery: customAccessQuery,
            }
          : baseQuery

      const res = await exportSolutionsCsv(query, idToken)

      if (res) {
        const exportBaseName =
          isFrom === 'custom-tag' && customTagName.trim()
            ? buildCustomTagExportBaseName(customTagName)
            : 'Solutions_Export'
        const { mimeType, filename } = getSolutionsExportDownloadMeta(
          res.headers as Record<string, string | undefined>,
          exportBaseName,
        )
        const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: mimeType })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      }
    }
  }
  const refreshVendorList = async (isSharedPortfolioEntry: boolean = false) => {
    if (isSharedPortfolioEntry) {
      await getSolutions()
      return
    }
    if (isFrom === 'custom-tag') {
      // Reload tag (solutionDocumentIds) so search re-scopes after remove/unfollow — same as UAT.
      await refetchCustomTagData?.()
      return
    }
    if (['my-following', 'my-followings', 'my-shared-portfolio', 'my-private-portfolio'].includes(isFrom)) {
      await getSolutions(true)
      refreshFilterData?.()
    }
  }
  const refreshFollowVendorList = async () => {
    if (isFrom === 'custom-tag') {
      await refetchCustomTagData?.()
    } else {
      await getSolutions(true)
      refreshFilterData?.()
    }
  }

  const freeSearchWithAllCTA = (
    <FreeSearchPopup
      heading={`You have reached your final search for the week.`}
      title={<>Register a free account on LTH to continue searching.</>}
      createAccountButton={true}
      premiumSignupButton={false}
      foundSolution={items?.length > 0}
    />
  )

  const freeSearchWithPremiumButton = (
    <FreeSearchPopup
      heading={`You have reached your final free search for the week.`}
      title={
        <>
          Sign up to LTH <span className="text-[#3329EF]">Premium</span> for unlimited searches.
        </>
      }
      createAccountButton={false}
      premiumSignupButton={true}
      foundSolution={items?.length > 0}
    />
  )
  return (
    <div className="flex flex-col flex-1">
      {compareInfos?.solutions?.length ? (
        <Drawer duration={250} hideScrollbars={true} onClose={closeDrawer} isVisible={isVisible}>
          <CompareModalContent
            onClose={closeDrawer}
            solutionType={
              compareInfos.solutions.length > 0 ? compareInfos.solutions[0].solutionType : IEnum_Solution_Type.Product
            }
          />
        </Drawer>
      ) : null}
      <Hidden mdDown>
        <FilterOptions filterOptions={filterPropsData} className="my-6" />

        <div className="flex flex-row justify-between items-center h-16 font-primary font-medium">
          {loading && (
            <div className="flex items-center">
              <TailSpin stroke="#011D58" height="2em" /> <div className="pl-3"> Loading...</div>
            </div>
          )}
          {!loading && (
            <div className="font-primary font-medium">
              {totalCount === 0 ? `${showNoMessage}` : `${totalCount} ${showFoundMessage}`}
            </div>
          )}
          <div className="flex flex-row items-center space-x-4">
            <SortbyDropdown
              className="w-54 font-primary font-medium"
              type="solutions"
              fromPage={isFrom}
              searchKeyword={keywordForDisplay}
            />
            {showExportButton && (
              <ThemeOnly theme="legaltech">
                <button
                  className={`bg-navy text-white py-2.5 px-4 rounded-full text-[14px] flex flex-row justify-center items-center disabled:opacity-50 disabled:cursor-not-allowed`}
                  onClick={exportSolutionsINCsv}
                  disabled={items?.length === 0}
                >
                  Export Solutions <IonIcon name="download" className="ml-2" />
                </button>
              </ThemeOnly>
            )}
          </div>
        </div>
        {loading && <LoadingAnimation></LoadingAnimation>}
        {!loading && (
          <div>
            <VendorList
              items={displayItems}
              companyName={companyName}
              onRefresh={refreshVendorList}
              isFrom={isFrom}
              followOnly={followOnly}
              refreshFollowVendorList={refreshFollowVendorList}
              sharedPortfolioId={sharedPortfolioId}
              sharedPortfolioSlug={sharedPortfolioSlug}
              showPortfolioCustomTags={
                (['my-private-portfolio', 'my-shared-portfolio', 'custom-tag', 'my-followings'].includes(isFrom) &&
                  isPremium) ||
                false
              }
              refetchSideBarTagsData={refetchSideBarTagsData}
            />
            {isNonLogin && !isFreeSearch && isFreeSearch != null && freeSearchWithAllCTA}
            {!isNonLogin && !isFreeSearch && isFreeSearch != null && freeSearchWithPremiumButton}
            {totalCount > 0 && (!SHOW_FREE_SEARCH || (SHOW_FREE_SEARCH && (isFreeSearch == null || isFreeSearch))) && (
              <Pagination
                postsPerPage={postsPerPage}
                totalPosts={totalCount}
                paginateBack={paginateBack}
                paginateFront={paginateFront}
                paginate={paginate}
                currentPage={currentPage}
                className="pt-[1.875rem]"
              />
            )}
          </div>
        )}
      </Hidden>
      <Hidden mdUp>
        <div className="flex flex-row justify-between md:items-center md:h-16 font-primary font-medium md:mb-2 pt-5">
          <div className="flex flex-col md:mt-2">
            <Filter onClickFilter={() => setShowFilterDlg(true)} />
            {loading && (
              <div className="flex items-center">
                <TailSpin stroke="#011D58" height="2em" /> <div className="pl-3"> Loading...</div>
              </div>
            )}
            {!loading && (
              <div className="font-primary font-medium text-[12px] leading-[26px]">
                {totalCount === 0 ? `${showNoMessage}` : `${totalCount} ${showFoundMessage}`}
              </div>
            )}
          </div>

          <SortbyDropdown
            className="w-54 font-primary font-medium md:mt-[-21px]"
            type="solutions"
            fromPage={isFrom}
            searchKeyword={keywordForDisplay}
          />
        </div>
        <FilterOptions filterOptions={filterPropsData} className="mt-[16px] mb-[20px]" />

        {loading && <LoadingAnimation></LoadingAnimation>}
        {!loading && (
          <div>
            <VendorList
              items={displayItems}
              companyName={companyName}
              onRefresh={refreshVendorList}
              isFrom={isFrom}
              followOnly={followOnly}
              refreshFollowVendorList={refreshFollowVendorList}
              sharedPortfolioId={sharedPortfolioId}
              sharedPortfolioSlug={sharedPortfolioSlug}
              showPortfolioCustomTags={
                (['my-private-portfolio', 'my-shared-portfolio', 'my-following', 'custom-tag'].includes(isFrom) &&
                  isPremiumWithPremiumOrganization) ||
                false
              }
              refetchSideBarTagsData={refetchSideBarTagsData}
            />
            {isNonLogin && !isFreeSearch && isFreeSearch != null && freeSearchWithAllCTA}
            {!isNonLogin && !isFreeSearch && isFreeSearch != null && freeSearchWithPremiumButton}
            {totalCount > 0 && (!SHOW_FREE_SEARCH || (SHOW_FREE_SEARCH && (isFreeSearch == null || isFreeSearch))) && (
              <Pagination
                postsPerPage={postsPerPage}
                totalPosts={totalCount}
                paginateBack={paginateBack}
                paginateFront={paginateFront}
                paginate={paginate}
                currentPage={currentPage}
                className="pt-[1.875rem]"
              />
            )}
          </div>
        )}
      </Hidden>

      <Modal open={showFilterDlg}>
        <div className="flex flex-col w-full h-full bg-white p-[17px]">
          <div className="flex flex-row mb-[13px]">
            <p className="font-secondary font-bold text-[20px] text-darkgray items-center">Filter</p>
            <div className="ml-auto" onClick={() => setShowFilterDlg(false)}>
              <IonIcon name="close-outline" className="!h-[30px] !w-[30px] text-[black]" />
            </div>
          </div>
          <SearchField className="mb-[23px]" />
          <Sidebar
            filterOptions={filterPropsData}
            defaultSelectFilterIdx={defaultSelectFilterIdx}
            sharedPortfolioId={sharedPortfolioId}
            isSharedPortfolio={isSharedPortfolio}
            onCustomTagsChanged={refetchSideBarTagsData}
          />
          <button
            className="text-white font-secondary text-[14px] font-bold text-center rounded-[22.5px] px-[25px] bg-navy mt-auto mb-[15px] h-[50px] !important"
            onClick={() => setShowFilterDlg(false)}
          >
            Apply Filters
          </button>
        </div>
      </Modal>
      <PremiumModal isOpen={showPremiumModal} onClose={handleClosePremiumModal} />
    </div>
  )
}
